import localDataFunctions from "@/localDataFunctions";
import axios from "axios";

export default {
  name: "JwtTokenHandler",
  mixins: [localDataFunctions],
  data: function () {
    return {
      requestInterceptorId: null,
      responseInterceptorId: null,
      isRenewingToken: false,
      bufferTime: 0,
      pendingRequests: new Map(),
    };
  },
  methods: {
    saveJwtToken(jwtToken, jwtTokenExpiration = null, bufferTime = null) {
      // Set buffer time with proper fallback
      this.bufferTime = bufferTime || 5; // Default to 5 minutes if not provided

      if (jwtToken && typeof jwtToken === "string" && jwtToken.trim() !== "") {
        try {
          this.saveToLocal("token", jwtToken, false);
          this.registerAxiosInterceptors(jwtToken);
        } catch (error) {
          return;
        }
      } else {
        return;
      }

      if (jwtTokenExpiration) {
        // Ensure the expiration date is stored in ISO string format (UTC)
        let expirationToStore;
        try {
          if (typeof jwtTokenExpiration === "string") {
            // If it's already a string, assume it's in correct format
            expirationToStore = jwtTokenExpiration;
          } else if (jwtTokenExpiration instanceof Date) {
            // If it's a Date object, convert to ISO string (UTC)
            expirationToStore = jwtTokenExpiration.toISOString();
          } else {
            // If it's something else, try to create a Date and convert to ISO string
            expirationToStore = new Date(jwtTokenExpiration).toISOString();
          }

          this.saveToLocal("tokenExpiration", expirationToStore, false);
        } catch (error) {
          // Silently handle expiration processing errors
        }
      }
    },
    getJwtToken() {
      try {
        const token = this.getFromLocal("token");
        if (token && typeof token === "string" && token.trim() !== "") {
          return token;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    getJwtTokenExpiration() {
      try {
        const expiration = this.getFromLocal("tokenExpiration");
        if (
          expiration &&
          typeof expiration === "string" &&
          expiration.trim() !== ""
        ) {
          return expiration;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    isTokenExpired() {
      const expiration = this.getJwtTokenExpiration();
      if (!expiration) {
        return true; // If no expiration date, consider it expired
      }

      // Parse the UTC date from localStorage
      const expirationDate = new Date(expiration);
      const currentDate = new Date();

      // Validate expiration date
      if (isNaN(expirationDate.getTime())) {
        return true;
      }

      // Ensure we're comparing in the same timezone (UTC)
      const expirationTimeUTC = expirationDate.getTime();
      const currentTimeUTC = currentDate.getTime();

      // Add a buffer of 5 minutes before actual expiration
      const bufferTime = (this.bufferTime || 5) * 60 * 1000; // Default to 5 minutes

      return currentTimeUTC >= expirationTimeUTC - bufferTime;
    },
    async refreshTokenIfNeeded() {
      if (this.isTokenExpired()) {
        // Prevent multiple concurrent renewal requests
        if (this.isRenewingToken) {
          // Wait for the ongoing renewal to complete
          while (this.isRenewingToken) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          // Always resolve - let the request proceed regardless
          return Promise.resolve();
        }

        this.isRenewingToken = true;

        try {
          // Attempt to renew the token
          const response = await this.renewJwtToken();

          // Check for both possible field names (token or jwtToken)
          const newToken = response.data.token || response.data.jwtToken;
          const newExpiration =
            response.data.expiration || response.data.jwtTokenExpiration;

          if (response && response.data && newToken) {
            // Successfully renewed, save the new token
            this.saveJwtToken(newToken, newExpiration);
            // this.$alertMsg("Session renewed successfully.");
            return Promise.resolve();
          } else {
            this.$alertMsg("Session renewal failed. Please login again.");
            // Cancel all pending requests
            this.cancelAllPendingRequests();
            this.deleteJwtToken();
            if (this.$router) {
              this.$router.push("/login");
            } else {
              window.location.href = "/login";
            }
            // Don't reject - let the request proceed (server will handle expired token)
            return Promise.resolve();
          }
        } catch (error) {
          this.$alertMsg("Session expired. Please login again.");
          // Cancel all pending requests
          this.cancelAllPendingRequests();
          // If renewal fails, delete token and redirect to login
          this.deleteJwtToken();
          if (this.$router) {
            this.$router.push("/login");
          } else {
            window.location.href = "/login";
          }
          // Don't reject - let the request proceed (server will handle expired token)
          return Promise.resolve();
        } finally {
          this.isRenewingToken = false;
        }
      }
      return Promise.resolve();
    },
    cancelAllPendingRequests() {
      this.pendingRequests.forEach((cancelToken, requestId) => {
        try {
          cancelToken.cancel("Token expired - request cancelled");
        } catch (error) {
          // Silently handle cancellation errors
        }
      });
      this.pendingRequests.clear();
    },
    async renewJwtToken() {
      const currentToken = this.getJwtToken();
      if (!currentToken) {
        throw new Error("No current token available for renewal");
      }

      try {
        // Make request to renew token using the current (expired) token
        const response = await axios.post(
          "/Account/RenewJwtToken",
          { TrustedComputer: this.$store.getters.getIsFieldWorker ?? false },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        return response;
      } catch (error) {
        throw error;
      }
    },
    checkForJwtToken() {
      let jwtToken = this.getFromLocal("token");
      let tokenExpiration = this.getFromLocal("tokenExpiration");

      if (jwtToken && typeof jwtToken === "string" && jwtToken.trim() !== "") {
        // Validate token is not expired (basic check without buffer)
        if (tokenExpiration) {
          try {
            const expirationDate = new Date(tokenExpiration);
            const currentDate = new Date();

            if (currentDate.getTime() >= expirationDate.getTime()) {
              this.deleteJwtToken();
              return false;
            }
          } catch (error) {
            this.deleteJwtToken();
            return false;
          }
        }

        this.registerAxiosInterceptors(jwtToken);
        return true;
      }

      return false;
    },
    deleteJwtToken() {
      this.removeFromLocal("token");
      this.removeFromLocal("tokenExpiration");

      // Remove interceptors when deleting token
      if (this.requestInterceptorId !== null) {
        axios.interceptors.request.eject(this.requestInterceptorId);
        this.requestInterceptorId = null;
      }
      if (this.responseInterceptorId !== null) {
        axios.interceptors.response.eject(this.responseInterceptorId);
        this.responseInterceptorId = null;
      }
    },
    registerAxiosInterceptors(jwtToken) {
      // Remove existing interceptors if they exist
      if (this.requestInterceptorId !== null) {
        axios.interceptors.request.eject(this.requestInterceptorId);
      }
      if (this.responseInterceptorId !== null) {
        axios.interceptors.response.eject(this.responseInterceptorId);
      }

      // Store reference to this for use in interceptor
      const self = this;

      this.requestInterceptorId = axios.interceptors.request.use(
        async (config) => {
          // Skip token renewal check for the renewal request itself
          if (config.url === "/Account/RenewJwtToken") {
            return config;
          }

          // Create cancel token for this request
          const CancelToken = axios.CancelToken;
          const source = CancelToken.source();
          config.cancelToken = source.token;

          // Generate unique request ID and track the request
          const requestId = Date.now() + Math.random();
          self.pendingRequests.set(requestId, source);

          // Store request ID in a custom property
          (config as any).requestId = requestId;

          // Check token expiration before each request
          // This will handle renewal transparently
          await self.refreshTokenIfNeeded();

          // Get the current token (might have been refreshed)
          const currentToken = self.getJwtToken();
          if (currentToken) {
            config.headers.Authorization = `Bearer ${currentToken}`;
          }

          config.headers["Content-Type"] = "application/json";

          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );

      this.responseInterceptorId = axios.interceptors.response.use(
        (response) => {
          // Clean up completed request
          const requestId = (response.config as any).requestId;
          if (requestId) {
            self.pendingRequests.delete(requestId);
          }
          return response;
        },
        (error) => {
          // Clean up failed request
          if (error.config) {
            const requestId = (error.config as any).requestId;
            if (requestId) {
              self.pendingRequests.delete(requestId);
            }
          }

          if (error.response && error.response.status === 401) {
            // Handle token expiration or unauthorized access
            self.$alertMsg("Session expired. Please login again.");
            // Cancel all pending requests
            self.cancelAllPendingRequests();
            self.deleteJwtToken();

            if (self.$router) {
              self.$router.push("/login");
            } else {
              window.location.href = "/login";
            }
          }
          return Promise.reject(error);
        }
      );
    },
  },
};
